import React from 'react';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormFooter, FormHeader, FormSection, Field, FormApi, ErrorMessage } from '@atlaskit/form';
import get from 'lodash/get';
import set from 'lodash/set';
import Textfield from '@atlaskit/textfield';
import {
  InfoPanel,
  RequiredFieldMessage,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  ErrorComponents,
} from '@css-xp/base';
import { AssignReservedCloudNameRequest } from '@atlassian/cssxp-typescommon';
import { reserveNewCloudSiteName, updateReservedCloudSiteName } from '@atlassian/temp-proxies';

interface Props {
  cloudName?: string;
  companyDomain?: string;
  companyName?: string;
  isUpdate?: boolean;
}

interface FormData {
  cloudName?: string;
  companyDomain?: string;
  companyName?: string;
  ticketUrl?: string;
}
interface ReserveNewCloudSiteRequest {
  cloudName: string;
  companyDomain?: string;
  companyName?: string;
  ticketUrl: string;
}
const AddNewSiteSidebarPane = ({ cloudName = '', companyName = '', companyDomain = '', isUpdate = false }: Props) => {
  const { closeSidebar } = useSidebar();
  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<ReserveNewCloudSiteRequest, any>({
    submitFormAction: isUpdate ? updateReservedCloudSiteName : reserveNewCloudSiteName,
    onComplete: () => {
      closeSidebar();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId(isUpdate ? 'update-reserved-cloud-site-success' : 'reserve-new-cloud-site-success')
      .withHeader(isUpdate ? 'Update reserved site' : 'Reserve new site')
      .withMessage(isUpdate ? 'Reserved cloud site successfully updated' : 'New cloud name successfully added')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const constructRequestData = (formData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(formData, key);
      set(transformedData, key, value);
    });

    const requestData: ReserveNewCloudSiteRequest = {
      cloudName: transformedData?.cloudName ?? '',
      companyDomain: transformedData?.companyDomain ?? '',
      companyName: transformedData?.companyName ?? '',
      ticketUrl: transformedData?.ticketUrl ?? '',
    };

    return requestData;
  };
  const validate = (name: string | undefined) => {
    const lettersNumbersAndDashes = name && /^[a-z0-9][-a-z0-9]*[a-z0-9]$/g.test(name);

    if (name && name?.length < 3) {
      return 'Name must be at least 3 characters long.';
    } else if (!lettersNumbersAndDashes) {
      return 'Name can only contain lowercase letters, numbers and dashes.';
    }
  };
  const handleFormSubmit = (formData: FormData, formApi: FormApi<FormData>) => {
    const requestData = constructRequestData(formData, formApi);
    submitForm(requestData);
  };
  return (
    <div style={{ maxWidth: '500px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={isUpdate ? 'Update reserved site' : 'Reserve new site'}></FormHeader>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  title="We're unable to assign this cloud name for the following reason"
                  message={formError}
                />
              )}
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title={
                        isUpdate
                          ? "We're unable to update this cloud site for the following reason"
                          : "We're unable to add this cloud name for the following reason"
                      }
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <Field name="siteUrl" label="Cloud Name" defaultValue={cloudName} validate={validate} isRequired={true}>
                {({ fieldProps, error }) => (
                  <>
                    {' '}
                    <Textfield {...fieldProps} name="cloudName" />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </Field>
              <Field name="cloudSite" label="Company Domain" defaultValue={companyDomain}>
                {({ fieldProps }) => <Textfield {...fieldProps} name="companyDomain" />}
              </Field>
              <Field name="cloudName" label="Company name" defaultValue={companyName}>
                {({ fieldProps }) => <Textfield {...fieldProps} name="companyName" />}
              </Field>
              <Field name="ticketUrl" label="Ticket URL" isRequired={true}>
                {({ fieldProps }) => <Textfield {...fieldProps} name="ticketUrl" />}
              </Field>
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={isProcessing}
                  testId="assign-site-name-btn"
                >
                  {isUpdate ? 'Update' : 'Save'}
                </LoadingButton>
                <Button appearance="subtle" onClick={() => closeSidebar()} testId="cancel">
                  Cancel
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default AddNewSiteSidebarPane;
